import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack} from '@mui/material';
import {forwardRef, useImperativeHandle, useState} from 'react';
import {useForm} from 'react-hook-form';
import {toast} from 'react-toastify';
import {useSetRecoilState} from 'recoil';

import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {useEventBus} from '../../../../hooks/useEventBus';
import {IServiceRequest} from '../../../../shared/types/serviceRequest.types';
import {EMPTY_SERVICE_REQUEST, serviceRequestsQueryTrigger} from '../../../../store/service-requests/atoms';
import {getServiceRequestDto} from '../../new/utils';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import PhoneInput from '../../../../ui/phone-input/PhoneInput';

interface Props {
  serviceRequest?: IServiceRequest;
}

export interface EditCustomerFormModalHandlers {
  handleClickOpen: VoidFunction;
}

interface FormValues {
  customer: string;
  customerPhone: string;
}

function prepareDefaultValues(serviceRequest?: IServiceRequest): FormValues {
  const sr = serviceRequest || EMPTY_SERVICE_REQUEST;

  return {
    customer: sr.customer || '',
    customerPhone: sr.customerPhone || '',
  };
}

export const EditCustomerFormModal = forwardRef<EditCustomerFormModalHandlers, Props>(({serviceRequest}, ref) => {
  const [open, setOpen] = useState(false);
  const bus = useEventBus();
  const triggerSRQuery = useSetRecoilState(serviceRequestsQueryTrigger);
  const defaultValues = prepareDefaultValues(serviceRequest);

  const {control, handleSubmit, reset} = useForm<FormValues>({
    defaultValues,
  });

  useImperativeHandle(ref, () => ({
    handleClickOpen: () => setOpen(true),
  }));

  const handleClose = () => {
    setOpen(false);
    reset(defaultValues);
  };

  const handleSave = handleSubmit(async (data) => {
    if (!serviceRequest) {
      handleClose();
      return;
    }

    try {
      await ServiceRequestService.update(
        getServiceRequestDto({
          ...serviceRequest,
          customer: data.customer.trim(),
          customerPhone: data.customerPhone ? data.customerPhone.trim() : null,
        }),
      );

      toast.success('Данные абонента успешно обновлены', {
        containerId: 'default',
        autoClose: 2000,
        hideProgressBar: true,
        pauseOnHover: false,
        draggable: false,
      });

      setTimeout(() => triggerSRQuery((v) => v + 1), 1000);
    } catch (e) {
      bus?.emit('error', [e]);
    } finally {
      handleClose();
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Данные абонента</DialogTitle>
      <DialogContent>
        <Stack mt={2} spacing={2}>
          <ControlledInput
            control={control}
            name="customer"
            label="Наименование абонента"
            size="small"
            fullWidth
            rules={{
              required: 'Обязательно для заполнения',
            }}
          />
          <PhoneInput
            control={control}
            name="customerPhone"
            label="Номер телефона"
            size="small"
            rules={{
              pattern: {
                value: /^(\+7 \([1-9]\d{2}\) \d{3}-\d{2}-\d{2}( #\d+)?$)/,
                message: 'Некорректный номер или добавочный',
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Ок
        </Button>
      </DialogActions>
    </Dialog>
  );
});
